import React from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import matter from 'gray-matter';
import { Helmet } from 'react-helmet-async';
import NavBar from '../../components/NavBar'; // Adjusted path based on new location
import AdSlot from '../../components/AdSlot';
import '../../styles/sitePages.css';
import './BlogPost.css';

// Load all markdown files statically (same pattern as Blog.jsx)
const markdownFiles = import.meta.glob('/src/content/posts/*.md', { eager: true, as: 'raw' });

// Create a lookup map of slug -> content
const blogPostsMap = Object.entries(markdownFiles).reduce((acc, [filePath, fileContents]) => {
  const { data, content } = matter(fileContents);
  // Extract slug from filePath, e.g., /src/content/posts/my-post.md -> my-post
  const slug = filePath.substring(filePath.lastIndexOf('/') + 1, filePath.lastIndexOf('.'));
  acc[slug] = { frontmatter: data, content };
  return acc;
}, {});

// Suggested posts (excluding the current one). The pick is seeded by the slug
// so it is the same in the prerendered HTML and on the client; Math.random
// here used to break hydration on every blog post.
function getSuggestedPosts(currentSlug, count = 2) {
  const allPosts = Object.entries(blogPostsMap)
    .filter(([postSlug]) => postSlug !== currentSlug)
    .map(([postSlug, { frontmatter }]) => ({ slug: postSlug, ...frontmatter }));

  let seed = 2166136261;
  for (let i = 0; i < currentSlug.length; i++) {
    seed = Math.imul(seed ^ currentSlug.charCodeAt(i), 16777619) >>> 0;
  }
  const next = () => {
    seed = (seed + 0x6D2B79F5) >>> 0;
    let t = seed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  // Seeded Fisher-Yates shuffle
  for (let i = allPosts.length - 1; i > 0; i--) {
    const j = Math.floor(next() * (i + 1));
    [allPosts[i], allPosts[j]] = [allPosts[j], allPosts[i]];
  }

  return allPosts.slice(0, count);
}

function BlogPostPage() {
  const { slug } = useParams();
  // Posts are bundled (eager glob), so look the post up synchronously. A
  // "Loading post..." first render would not match the prerendered HTML.
  const post = blogPostsMap[slug] || { frontmatter: {}, content: '' };
  const error = blogPostsMap[slug] ? null : 'Failed to load the blog post. It might not exist or there was a network error.';

  if (error) {
    return (
      <div>
        <NavBar />
        <div className="container blog-post-container blog-post-message">
          <p>{error}</p>
          <Link to="/blog" className="back-to-blog-link">← Back to Blog</Link>
        </div>
      </div>
    );
  }

  if (!post.frontmatter.title) {
    // Handles case where post might be found but frontmatter is missing or malformed
    return (
      <div>
        <NavBar />
        <div className="container blog-post-container blog-post-message">
          <p>Blog post not found or is improperly formatted.</p>
          <Link to="/blog" className="back-to-blog-link">← Back to Blog</Link>
        </div>
      </div>
    );
  }

  // Absolute URL for social/AI previews (og:image must not be relative)
  const absoluteImage = post.frontmatter.image
    ? `https://logicpuzzles.ca${post.frontmatter.image}`
    : 'https://logicpuzzles.ca/og-image.png';

  // Schema.org requires ISO 8601 dates; frontmatter stores "March 8, 2025" style strings
  const parsedDate = new Date(post.frontmatter.date);
  const isoDate = isNaN(parsedDate.getTime()) ? undefined : parsedDate.toISOString().split('T')[0];

  // Generate BlogPosting structured data for SEO
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.frontmatter.title,
    "description": post.frontmatter.excerpt || `Read about ${post.frontmatter.title} on the LogicPuzzles.ca blog.`,
    "image": absoluteImage,
    "datePublished": isoDate,
    "dateModified": isoDate,
    "inLanguage": "en",
    "author": {
      "@type": "Person",
      "name": post.frontmatter.author || "Ben Miller",
      "url": post.frontmatter.authorUrl || "https://www.linkedin.com/in/benmiller12/"
    },
    "publisher": {
      "@type": "Organization",
      "@id": "https://logicpuzzles.ca/#organization",
      "name": "Logic Puzzles CA",
      "url": "https://logicpuzzles.ca",
      "logo": {
        "@type": "ImageObject",
        "url": "https://logicpuzzles.ca/android-icon-192x192.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://logicpuzzles.ca/blog/${slug}`
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://logicpuzzles.ca" },
      { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://logicpuzzles.ca/blog" },
      { "@type": "ListItem", "position": 3, "name": post.frontmatter.title, "item": `https://logicpuzzles.ca/blog/${slug}` }
    ]
  };

  return (
    <div>
      <Helmet>
        <title>{post.frontmatter.title} - LogicPuzzles.ca Blog</title>
        <meta name="description" content={post.frontmatter.excerpt || `Read about ${post.frontmatter.title} on the LogicPuzzles.ca blog.`} />
        <link rel="canonical" href={`https://logicpuzzles.ca/blog/${slug}`} />
        <meta property="og:title" content={post.frontmatter.title} />
        <meta property="og:description" content={post.frontmatter.excerpt || `Read about ${post.frontmatter.title} on the LogicPuzzles.ca blog.`} />
        <meta property="og:url" content={`https://logicpuzzles.ca/blog/${slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={absoluteImage} />
        {isoDate && <meta property="article:published_time" content={isoDate} />}
        <meta property="article:author" content={post.frontmatter.author || 'Ben Miller'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.frontmatter.title} />
        <meta name="twitter:description" content={post.frontmatter.excerpt || `Read about ${post.frontmatter.title} on the LogicPuzzles.ca blog.`} />
        <meta name="twitter:image" content={absoluteImage} />
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      <NavBar />
      <div className="blog-post-page">
        <div className="container blog-post-container">
          <header className="blog-post-header">
            <Link to="/blog" className="eyebrow blog-post-kicker">Blog</Link>
            <h1>{post.frontmatter.title}</h1>
            <div className="blog-meta">
              <span className="blog-date">{post.frontmatter.date}</span>
              {post.frontmatter.readTime &&
                <span className="blog-read-time">{post.frontmatter.readTime}</span>
              }
              {post.frontmatter.author && (
                <span className="blog-author">
                  {post.frontmatter.authorUrl ? (
                    <a href={post.frontmatter.authorUrl} target="_blank" rel="noopener noreferrer">
                      {post.frontmatter.author}
                    </a>
                  ) : (
                    post.frontmatter.author
                  )}
                </span>
              )}
            </div>
          </header>

          {post.frontmatter.image && (
            <figure className="blog-post-hero">
              <img src={post.frontmatter.image} alt="" width="1200" height="630" decoding="async" />
            </figure>
          )}

          <article className="blog-post-content sp-prose">
            <ReactMarkdown>{post.content}</ReactMarkdown>
          </article>

          <AdSlot className="blog-post-ad" pushKey={slug} />

          {/* Read Next Section */}
          <section className="read-next-section">
            <h2>Read Next</h2>
            <div className="read-next-grid">
              {getSuggestedPosts(slug, 2).map((suggestedPost) => (
                <Link
                  key={suggestedPost.slug}
                  to={`/blog/${suggestedPost.slug}`}
                  className="read-next-card"
                >
                  {suggestedPost.image && (
                    <div className="read-next-image">
                      <img src={suggestedPost.image} alt={suggestedPost.title} loading="lazy" />
                    </div>
                  )}
                  <div className="read-next-content">
                    <h3>{suggestedPost.title}</h3>
                    <p>{suggestedPost.excerpt}</p>
                    <span className="read-next-meta">
                      {suggestedPost.date} · {suggestedPost.readTime}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>

          <Link to="/blog" className="back-to-blog-link">← Back to Blog</Link>
        </div>
      </div>
    </div>
  );
}

export default BlogPostPage; 